import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, Contrast, RotateCcw, Image as ImageIcon } from 'lucide-react';

type ConversionMode = 'grayscale' | 'bw' | 'sepia' | 'partial';
type GrayscaleAlgorithm = 'luminosity' | 'average' | 'lightness';
type OutputFormat = 'png' | 'jpg';

interface ModeConfig {
  value: ConversionMode;
  label: string;
  description: string;
}

const MODES: ModeConfig[] = [
  { value: 'grayscale', label: 'Grayscale', description: 'Standard luminosity conversion' },
  { value: 'bw', label: 'Black & White', description: 'Threshold-based two-tone' },
  { value: 'sepia', label: 'Sepia', description: 'Warm vintage brown tone' },
  { value: 'partial', label: 'Partial Desat', description: 'Adjustable saturation removal' },
];

const ALGORITHMS: { value: GrayscaleAlgorithm; label: string; description: string }[] = [
  { value: 'luminosity', label: 'Luminosity', description: '0.299R + 0.587G + 0.114B' },
  { value: 'average', label: 'Average', description: '(R + G + B) / 3' },
  { value: 'lightness', label: 'Lightness', description: '(max + min) / 2' },
];

const GrayscaleImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [mode, setMode] = useState<ConversionMode>('grayscale');
  const [algorithm, setAlgorithm] = useState<GrayscaleAlgorithm>('luminosity');
  const [intensity, setIntensity] = useState(100);
  const [bwThreshold, setBwThreshold] = useState(128);
  const [sepiaIntensity, setSepiaIntensity] = useState(80);
  const [saturationLevel, setSaturationLevel] = useState(0);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [jpgQuality, setJpgQuality] = useState(92);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => setImgElement(img);
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const getGray = useCallback((r: number, g: number, b: number): number => {
    switch (algorithm) {
      case 'luminosity': return 0.299 * r + 0.587 * g + 0.114 * b;
      case 'average': return (r + g + b) / 3;
      case 'lightness': return (Math.max(r, g, b) + Math.min(r, g, b)) / 2;
    }
  }, [algorithm]);

  const processImage = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(img, 0, 0);

    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const blendFactor = intensity / 100;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];

        if (mode === 'grayscale') {
          const gray = getGray(r, g, b);
          data[i] = Math.round(r + (gray - r) * blendFactor);
          data[i + 1] = Math.round(g + (gray - g) * blendFactor);
          data[i + 2] = Math.round(b + (gray - b) * blendFactor);
        } else if (mode === 'bw') {
          const gray = getGray(r, g, b);
          const val = gray > bwThreshold ? 255 : 0;
          data[i] = val;
          data[i + 1] = val;
          data[i + 2] = val;
        } else if (mode === 'sepia') {
          const gray = getGray(r, g, b);
          const factor = sepiaIntensity / 100;
          // Sepia tint: blend grayscale with sepia tone (112, 66, 20)
          const sepiaR = gray + (112 - gray) * factor * 0.4;
          const sepiaG = gray + (66 - gray) * factor * 0.4;
          const sepiaB = gray + (20 - gray) * factor * 0.4;
          data[i] = Math.min(255, Math.round(sepiaR));
          data[i + 1] = Math.min(255, Math.round(sepiaG));
          data[i + 2] = Math.min(255, Math.round(sepiaB));
        } else if (mode === 'partial') {
          const gray = getGray(r, g, b);
          const sat = saturationLevel / 100;
          data[i] = Math.round(gray + (r - gray) * sat);
          data[i + 1] = Math.round(gray + (g - gray) * sat);
          data[i + 2] = Math.round(gray + (b - gray) * sat);
        }
      }

      ctx.putImageData(imageData, 0, 0);
    } catch (err) {
      setError('Failed to process image. The image may be too large or from a different origin.');
    }
  }, [mode, algorithm, intensity, bwThreshold, sepiaIntensity, saturationLevel, getGray]);

  // Live preview
  useEffect(() => {
    if (!imgElement || !previewCanvasRef.current) return;
    processImage(previewCanvasRef.current, imgElement);
  }, [imgElement, processImage]);

  const downloadResult = useCallback(() => {
    if (!imgElement || !file) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    processImage(canvas, imgElement);

    const mimeType = outputFormat === 'jpg' ? 'image/jpeg' : 'image/png';
    const quality = outputFormat === 'jpg' ? jpgQuality / 100 : undefined;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const name = file.name.replace(/\.[^.]+$/, '');
      const suffix = mode === 'bw' ? 'bw' : mode;
      const ext = outputFormat === 'jpg' ? 'jpg' : 'png';
      link.download = `${name}_${suffix}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    }, mimeType, quality);
  }, [imgElement, file, processImage, mode, outputFormat, jpgQuality]);

  const reset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setMode('grayscale');
    setAlgorithm('luminosity');
    setIntensity(100);
    setBwThreshold(128);
    setSepiaIntensity(80);
    setSaturationLevel(0);
    setOutputFormat('png');
    setJpgQuality(92);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  return (
    <div className="space-y-6">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-gray-200 rounded-full">
            <Contrast className="w-8 h-8 text-gray-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Grayscale & Tone Converter</h2>
        <p className="text-gray-600">
          Convert images to grayscale, black and white, sepia, or partial desaturation.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          {/* Mode Selection */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Conversion Mode</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {MODES.map((m) => (
                <button
                  key={m.value}
                  onClick={() => setMode(m.value)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border-2 transition-colors text-center ${
                    mode === m.value
                      ? 'border-gray-700 bg-gray-100 text-gray-900'
                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                  }`}
                >
                  <span className="font-medium text-sm">{m.label}</span>
                  <span className="text-xs opacity-75 leading-tight">{m.description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Algorithm Selector (for grayscale and bw) */}
          {(mode === 'grayscale' || mode === 'bw' || mode === 'partial') && (
            <div className="bg-gray-50 rounded-xl p-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">Grayscale Algorithm</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {ALGORITHMS.map((a) => (
                  <button
                    key={a.value}
                    onClick={() => setAlgorithm(a.value)}
                    className={`flex flex-col items-center gap-0.5 p-2.5 rounded-lg border-2 transition-colors text-center ${
                      algorithm === a.value
                        ? 'border-gray-700 bg-gray-100 text-gray-900'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                    }`}
                  >
                    <span className="font-medium text-sm">{a.label}</span>
                    <span className="text-xs opacity-60 font-mono">{a.description}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Mode-specific Controls */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-3">
            {mode === 'grayscale' && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Grayscale Intensity</label>
                  <span className="text-sm font-bold text-gray-900">{intensity}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={intensity}
                  onChange={(e) => setIntensity(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-700"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                  <span>0% (original)</span>
                  <span>100% (full grayscale)</span>
                </div>
              </div>
            )}

            {mode === 'bw' && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Threshold</label>
                  <span className="text-sm font-bold text-gray-900">{bwThreshold}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={255}
                  value={bwThreshold}
                  onChange={(e) => setBwThreshold(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-700"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                  <span>0 (all white)</span>
                  <span>255 (all black)</span>
                </div>
              </div>
            )}

            {mode === 'sepia' && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Sepia Intensity</label>
                  <span className="text-sm font-bold text-gray-900">{sepiaIntensity}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={sepiaIntensity}
                  onChange={(e) => setSepiaIntensity(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-700"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                  <span>0% (grayscale)</span>
                  <span>100% (full sepia)</span>
                </div>
              </div>
            )}

            {mode === 'partial' && (
              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-sm font-medium text-gray-700">Saturation Level</label>
                  <span className="text-sm font-bold text-gray-900">{saturationLevel}%</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={saturationLevel}
                  onChange={(e) => setSaturationLevel(parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-700"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-0.5">
                  <span>0% (full grayscale)</span>
                  <span>100% (original color)</span>
                </div>
              </div>
            )}
          </div>

          {/* Output Format */}
          <div className="bg-gray-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">Output Format</label>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex gap-2">
                {(['png', 'jpg'] as OutputFormat[]).map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => setOutputFormat(fmt)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium uppercase transition-colors ${
                      outputFormat === fmt
                        ? 'bg-gray-800 text-white'
                        : 'bg-white border border-gray-200 text-gray-700 hover:border-gray-400'
                    }`}
                  >
                    {fmt}
                  </button>
                ))}
              </div>
              {outputFormat === 'jpg' && (
                <div className="flex-1 min-w-[200px]">
                  <div className="flex justify-between items-center mb-1">
                    <label className="text-xs font-medium text-gray-600">JPG Quality</label>
                    <span className="text-xs font-bold text-gray-900">{jpgQuality}%</span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={100}
                    value={jpgQuality}
                    onChange={(e) => setJpgQuality(parseInt(e.target.value))}
                    className="w-full h-1.5 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-gray-700"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Before/After Previews */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                <ImageIcon className="w-4 h-4" />
                Original
              </h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
                {originalPreview && (
                  <img src={originalPreview} alt="Original" className="max-w-full max-h-[400px] object-contain" />
                )}
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                <Contrast className="w-4 h-4" />
                {mode === 'grayscale' && 'Grayscale'}
                {mode === 'bw' && 'Black & White'}
                {mode === 'sepia' && 'Sepia'}
                {mode === 'partial' && 'Partial Desaturation'}
              </h3>
              <div className="bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[200px]">
                <canvas ref={previewCanvasRef} className="max-w-full max-h-[400px] object-contain" />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={downloadResult}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gray-800 text-white font-medium rounded-xl hover:bg-gray-900 transition-colors"
            >
              <Download className="w-5 h-5" />
              Download {mode === 'bw' ? 'B&W' : mode.charAt(0).toUpperCase() + mode.slice(1)} Image
            </button>
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
              title="Reset"
            >
              <RotateCcw className="w-5 h-5" />
              Reset
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrayscaleImage;
